// @ts-check

/**
 * @file Stores and hydrates the local session token Option.
 */

import FrontendModelBase from "velocious/build/src/frontend-models/base.js"

import {flushLocalDatabaseWrites} from "./local-database-persistence.js"
import Option from "../models/option.js"
import {UnsyncedReplicaMutationsError, sealReplica} from "../database/replica-lifecycle.js"

/** @typedef {import("velocious/build/src/database/migrator/types.js").RequireMigrationContextType} MigrationContext */

/** Name of the request header that carries the stored session token. */
export const SESSION_TOKEN_HEADER = "x-awesome-tasks-session"

/**
 * Session-token persistence backed by the local Velocious `Option` SQLite
 * table. The in-memory `cachedToken` is hydrated once at app boot via
 * `hydrateSessionToken()` so the Velocious transport `requestHeaders` hook
 * — which is called synchronously per request — can read the token without
 * awaiting the database.
 */

const STORAGE_KEY = "sessionToken"
const EXPIRES_AT_KEY = "sessionExpiresAt"
const USER_ID_KEY = "sessionUserId"

/** @type {string | null} */
let cachedToken = null
/** @type {Date | null} */
let cachedExpiresAt = null
/** @type {string | null} */
let cachedUserId = null
/** @type {boolean} */
let hydrated = false

/**
 * Reads the stored session token into the in-memory cache. Must be awaited
 * once after the Velocious database finishes loading, before any screen
 * issues a frontend-model request.
 * @param {{migrations?: MigrationContext}} [options] Optional project migration context for non-Metro runtimes.
 * @returns {Promise<void>}
 * @rejects {Error} When storage hydration, safe replica inspection, or credential clearing fails.
 */
export async function hydrateSessionToken({migrations} = {}) {
  const [tokenOption, expiresAtOption, userIdOption] = await Promise.all([
    Option.findBy({key: STORAGE_KEY}),
    Option.findBy({key: EXPIRES_AT_KEY}),
    Option.findBy({key: USER_ID_KEY})
  ])

  cachedToken = tokenOption?.value() || null
  cachedExpiresAt = parseStoredExpiresAt(expiresAtOption?.value() || null)
  cachedUserId = userIdOption?.value() || null
  hydrated = true

  // A persisted token is offline-usable only when it carries a syntactically
  // valid, future server-stamped expiry. Missing or malformed legacy state
  // fails closed so it can never unlock a cached profile indefinitely.
  if (cachedToken && (!cachedExpiresAt || cachedExpiresAt.getTime() <= Date.now())) {
    try {
      await sealReplica({migrations})
    } catch (error) {
      // An expired credential must never keep the startup gate pending. Pending
      // intent remains protected by the retained project database; only the
      // unusable credential is cleared so the same owner can reauthenticate.
      if (!(error instanceof UnsyncedReplicaMutationsError)) throw error
    }
    await clearSessionToken()
  } else {
    syncWebsocketSessionTokenMetadata()
  }
}

/**
 * Synchronous read used by the Velocious transport `requestHeaders` hook.
 * Returns null until `hydrateSessionToken()` has completed.
 * @returns {string | null} Cached token when the user is signed in.
 */
export function getSessionTokenSync() {
  return cachedToken
}

/**
 * Returns the durable user identity bound to the cached session token.
 * @returns {string | null} Stable user ID when the session is bound to an owner.
 */
export function getSessionUserIdSync() {
  return cachedToken ? cachedUserId : null
}

/**
 * Reads the session token after hydrating the cache if needed.
 * @returns {Promise<string | null>} Stored token when the user is signed in.
 */
export async function getSessionToken() {
  if (!hydrated) await hydrateSessionToken()

  return cachedToken
}

/**
 * Persists a new session token and its server-stamped expiry, then updates
 * the in-memory cache.
 * @param {{token: string, expiresAt: string | Date | null, userId: string | number}} args Session payload from the backend.
 * @returns {Promise<void>}
 * @rejects {Error} When the server expiry is missing, malformed, expired, or persistence fails.
 */
export async function setSessionToken({token, expiresAt, userId}) {
  const serializedExpiresAt = serializeExpiresAt(expiresAt)
  const parsedExpiresAt = parseStoredExpiresAt(serializedExpiresAt)
  if (!parsedExpiresAt || parsedExpiresAt.getTime() <= Date.now()) {
    throw new Error("Session token requires a valid future server-stamped expiry")
  }

  const tokenOption = await Option.findOrInitializeBy({key: STORAGE_KEY})
  tokenOption.assign({value: token})
  await tokenOption.save()

  const expiresAtOption = await Option.findOrInitializeBy({key: EXPIRES_AT_KEY})
  expiresAtOption.assign({value: serializedExpiresAt})
  await expiresAtOption.save()
  const userIdOption = await Option.findOrInitializeBy({key: USER_ID_KEY})
  userIdOption.assign({value: String(userId)})
  await userIdOption.save()
  await flushLocalDatabaseWrites()

  cachedToken = token
  cachedExpiresAt = parsedExpiresAt
  cachedUserId = String(userId)
  hydrated = true
  syncWebsocketSessionTokenMetadata()
}

/**
 * Clears the persisted session token + expiry, plus the in-memory cache.
 * @returns {Promise<void>}
 */
export async function clearSessionToken() {
  const [tokenOption, expiresAtOption, userIdOption] = await Promise.all([
    Option.findBy({key: STORAGE_KEY}),
    Option.findBy({key: EXPIRES_AT_KEY}),
    Option.findBy({key: USER_ID_KEY})
  ])

  if (tokenOption) await tokenOption.destroy()
  if (expiresAtOption) await expiresAtOption.destroy()
  if (userIdOption) await userIdOption.destroy()
  await flushLocalDatabaseWrites()

  cachedToken = null
  cachedExpiresAt = null
  cachedUserId = null
  hydrated = true
  syncWebsocketSessionTokenMetadata()
}

/**
 * Mirrors the current token into Velocious websocket metadata for channel auth.
 * @returns {void} Nothing.
 */
export function syncWebsocketSessionTokenMetadata() {
  FrontendModelBase.setWebsocketMetadata(SESSION_TOKEN_HEADER, cachedToken)
}

/**
 * Normalizes a server-supplied expiry into the ISO string we persist.
 * @param {string | Date | null} value Server payload.
 * @returns {string | null} ISO timestamp or null.
 */
function serializeExpiresAt(value) {
  if (!value) return null
  if (value instanceof Date) return value.toISOString()
  return value
}

/**
 * Parses an Option-stored expiry back into a Date.
 * @param {string | null} value Stored ISO timestamp or null.
 * @returns {Date | null} Parsed Date or null when the stored value is unusable.
 */
function parseStoredExpiresAt(value) {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}
